import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { coreServices, pageSeo, siteInfo } from "../data";

function setMetaTag(selector, attributes) {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
}

function setCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
}

function setStructuredData(data) {
  const scriptId = "higa-page-structured-data";
  let script = document.getElementById(scriptId);

  if (!script) {
    script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  city: "",
  message: "",
  website: "",
};

function formatWorkingHours(workingHours) {
  if (!workingHours) {
    return "Mon – Sat: 9:00 AM – 6:30 PM IST";
  }

  if (typeof workingHours === "string") {
    return workingHours;
  }

  if (typeof workingHours === "object") {
    const weekdays = workingHours.weekdays || "Mon – Sat";
    const timing = workingHours.timing || "9:00 AM – 6:30 PM";
    const timezone = workingHours.timezone || "IST";

    return `${weekdays}: ${timing} ${timezone}`.trim();
  }

  return String(workingHours);
}

export default function Contact() {
  const { toast } = useToast();

  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = pageSeo.contact.title;

    setMetaTag('meta[name="description"]', {
      name: "description",
      content: pageSeo.contact.description,
    });

    setMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });

    setMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: pageSeo.contact.title,
    });

    setMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: pageSeo.contact.description,
    });

    setMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: pageSeo.contact.canonical,
    });

    setMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: `${siteInfo.url}/higa-logo.png`,
    });

    setMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });

    setMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: pageSeo.contact.title,
    });

    setMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: pageSeo.contact.description,
    });

    setCanonical(pageSeo.contact.canonical);

    setStructuredData({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact HIG Automation",
      description: pageSeo.contact.description,
      url: pageSeo.contact.canonical,
      mainEntity: {
        "@type": "Organization",
        name: siteInfo.legalName,
        url: siteInfo.url,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Plot No. 121, Kaluwas Road",
          addressLocality: "Rewari",
          addressRegion: "Haryana",
          postalCode: "123401",
          addressCountry: "IN",
        },
      },
    });
  }, []);

  const handleChange = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.service) {
      toast({
        title: "Please select a service",
        description:
          "Choose the service you are interested in before submitting the enquiry.",
        variant: "destructive",
      });

      return;
    }

    const selectedService = coreServices.find(
      (service) => service.slug === form.service,
    );

    const serviceName =
      form.service === "other"
        ? "Other / Custom Requirement"
        : selectedService?.title || form.service;

    setIsSending(true);

    try {
      const response = await fetch("/send-quote.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: new URLSearchParams({
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          service: serviceName,
          city: form.city,
          message: form.message,
          website: form.website,
        }).toString(),
      });

      let result;

      try {
        result = await response.json();
      } catch {
        throw new Error(
          "The server returned an unexpected response. Please try again.",
        );
      }

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Unable to send your enquiry right now.",
        );
      }

      toast({
        title: "Thank you!",
        description:
          "Your enquiry has been submitted successfully. Our sales team will contact you shortly.",
      });

      setSubmitted(true);
      setForm(initialForm);
    } catch (error) {
      toast({
        title: "Submission Failed",
        description:
          error.message ||
          "Please try again later or email us directly at sales@higautomation.com.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative bg-[#0a1929] text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1929] to-[#14213d]" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-40 bg-[#c8102e]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-[#ff6b7a] font-bold text-xs uppercase tracking-[0.3em] mb-4">
            Contact Us
          </div>

          <h1 className="font-black text-5xl lg:text-6xl leading-tight tracking-tight mb-5">
            Let&apos;s Build Something Great Together.
          </h1>

          <p className="text-slate-300 max-w-2xl text-lg">
            Ready to modernise your factory? Share your requirements below and
            our team will respond within 24 hours.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          {/* CONTACT INFORMATION */}
          <div className="lg:col-span-5">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">
              Get In Touch
            </div>

            <h2 className="font-black text-3xl lg:text-4xl text-[#0a1929] tracking-tight leading-tight mb-8">
              Talk To An Engineer
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#c8102e]/10 text-[#c8102e] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>

                <div>
                  <div className="font-bold text-[#0a1929] mb-1">
                    Head Office &amp; Factory
                  </div>

                  <p className="text-slate-600">{siteInfo.address}</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#c8102e]/10 text-[#c8102e] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>

                <div>
                  <div className="font-bold text-[#0a1929] mb-1">Phone</div>

                  <a
                    href={`tel:${siteInfo.phone}`}
                    className="text-slate-600 hover:text-[#c8102e]"
                  >
                    {siteInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#c8102e]/10 text-[#c8102e] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>

                <div>
                  <div className="font-bold text-[#0a1929] mb-1">Email</div>

                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="text-slate-600 hover:text-[#c8102e]"
                  >
                    {siteInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#c8102e]/10 text-[#c8102e] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>

                <div>
                  <div className="font-bold text-[#0a1929] mb-1">
                    Working Hours
                  </div>

                  <p className="text-slate-600">
                    Mon – Sat: 9:00 AM – 6:30 PM IST
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 aspect-[4/3] w-full bg-slate-200 overflow-hidden border border-slate-200">
              <iframe
                title="HIG Automation location in Rewari, Haryana"
                src="https://www.google.com/maps?q=Rewari+Haryana+India&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

          {/* REQUEST A QUOTE FORM */}
          <div className="lg:col-span-7">
            <div className="bg-slate-50 border-t-4 border-[#c8102e] p-8 lg:p-10">
              <h2 className="font-black text-2xl text-[#0a1929] mb-2">
                Request A Quote
              </h2>

              <p className="text-slate-600 mb-8 text-sm">
                Fill out the form and our sales team will review your
                requirement.
              </p>

              {submitted ? (
                <div className="bg-white border border-green-200 p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />

                  <h3 className="font-black text-[#0a1929] text-xl mb-2">
                    Enquiry Received!
                  </h3>

                  <p className="text-slate-600 mb-6">
                    Our sales and engineering team will contact you shortly.
                  </p>

                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="rounded-none"
                  >
                    Submit Another
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="grid sm:grid-cols-2 gap-5"
                >
                  {/* HONEYPOT SPAM FIELD */}
                  <div
                    aria-hidden="true"
                    className="absolute -left-[9999px] w-px h-px overflow-hidden"
                  >
                    <Label htmlFor="website">Website</Label>

                    <Input
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(event) =>
                        handleChange("website", event.target.value)
                      }
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="name"
                      className="text-xs font-bold uppercase tracking-wider text-[#0a1929]"
                    >
                      Contact Name*
                    </Label>

                    <Input
                      id="name"
                      required
                      value={form.name}
                      onChange={(event) =>
                        handleChange("name", event.target.value)
                      }
                      className="mt-2 rounded-none bg-white border-slate-300 focus:border-[#c8102e] h-11"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="company"
                      className="text-xs font-bold uppercase tracking-wider text-[#0a1929]"
                    >
                      Business Name*
                    </Label>

                    <Input
                      id="company"
                      required
                      value={form.company}
                      onChange={(event) =>
                        handleChange("company", event.target.value)
                      }
                      className="mt-2 rounded-none bg-white border-slate-300 focus:border-[#c8102e] h-11"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="email"
                      className="text-xs font-bold uppercase tracking-wider text-[#0a1929]"
                    >
                      Email*
                    </Label>

                    <Input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(event) =>
                        handleChange("email", event.target.value)
                      }
                      className="mt-2 rounded-none bg-white border-slate-300 focus:border-[#c8102e] h-11"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="phone"
                      className="text-xs font-bold uppercase tracking-wider text-[#0a1929]"
                    >
                      Phone*
                    </Label>

                    <Input
                      id="phone"
                      required
                      value={form.phone}
                      onChange={(event) =>
                        handleChange("phone", event.target.value)
                      }
                      className="mt-2 rounded-none bg-white border-slate-300 focus:border-[#c8102e] h-11"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#0a1929]">
                      Service Interested In*
                    </Label>

                    <Select
                      value={form.service}
                      onValueChange={(value) =>
                        handleChange("service", value)
                      }
                    >
                      <SelectTrigger className="mt-2 rounded-none bg-white border-slate-300 h-11">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>

                      <SelectContent>
                        {coreServices.map((service) => (
                          <SelectItem
                            key={service.slug}
                            value={service.slug}
                          >
                            {service.title}
                          </SelectItem>
                        ))}

                        <SelectItem value="other">
                          Other / Custom Requirement
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label
                      htmlFor="city"
                      className="text-xs font-bold uppercase tracking-wider text-[#0a1929]"
                    >
                      City, State*
                    </Label>

                    <Input
                      id="city"
                      required
                      value={form.city}
                      onChange={(event) =>
                        handleChange("city", event.target.value)
                      }
                      className="mt-2 rounded-none bg-white border-slate-300 focus:border-[#c8102e] h-11"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label
                      htmlFor="message"
                      className="text-xs font-bold uppercase tracking-wider text-[#0a1929]"
                    >
                      Brief Description*
                    </Label>

                    <Textarea
                      id="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={(event) =>
                        handleChange("message", event.target.value)
                      }
                      className="mt-2 rounded-none bg-white border-slate-300 focus:border-[#c8102e]"
                      placeholder="Tell us about your project, requirements and timeline..."
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Button
                      type="submit"
                      disabled={isSending}
                      className="bg-[#c8102e] hover:bg-[#a80d26] text-white rounded-none px-10 py-6 text-sm font-bold uppercase tracking-wide gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSending ? "Sending Enquiry..." : "Submit Enquiry"}

                      {!isSending && <ArrowRight className="w-4 h-4" />}
                    </Button>

                    <p className="text-xs text-slate-500 mt-4">
                      Your enquiry will be sent to our sales team at{" "}
                      <strong>sales@higautomation.com</strong>.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}